import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Tooltip } from '@mic3/platform-ui';

import Icon from 'app/components/atoms/Icon/Icon';
import { formatThreadTime, formatThreadDateTime } from 'app/utils/chat/chatUtils';

// Shared bubble that wraps the message body + this summary into one rounded
// container with a shared background (Figma). Reused by AliveChatItem (paper) and
// ChatItem (message) — `surface` picks the matching theme bg token for each.
export const MessageBubble = styled.div`
    background: ${({ theme, surface }) => theme.material.colors.background[surface === 'message' ? 'message' : 'paper']};
    border-radius: 6px;
    padding-bottom: 8px;
    /* The inner MessageText (cm-s-* container) paints the same bubble bg; with the
       semi-transparent 'message' surface the two layers stack and darken the text region.
       Let this bubble own the single bg so text + summary read as one container. */
    & .cm-s-idea,
    & .cm-s-lucario {
        background: transparent;
    }
    /* breathing room around the REPLY row so it isn't glued to the box bottom (Figma) */
    & .thread-summary {
        padding: 8px 16px 12px;
        margin-top: 8px;
    }
`;

/* Figma (5043-87284): 32px row inside the message bubble, REPLY button 53x32 */
const SummaryRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    height: 32px;
`;

// Color comes from the theme (primary.main = #1248BD light / #3D64B7 dark) via the
// MUI contained-primary Button — no hardcoded hex, stays theme-managed.
const ReplyButton = styled(Button)`
    && {
        width: 53px;
        min-width: 53px;
        height: 32px;
        padding: 0;
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.75px;
        cursor: pointer;
    }
`;

const MetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: ${({ theme }) => theme.material.colors.text.secondary};
    /* mdi glyphs carry extra line-height — collapse so they center against the 12px text */
    & .Icon,
    & .Icon:before {
        display: inline-flex;
        align-items: center;
        line-height: 1;
    }
`;

// Static tooltip labels — match Rocket.Chat (no count interpolation).
export const THREAD_META_TOOLTIP = { replies: 'Replies', participants: 'Participants' };

// Shared icon+value meta item with tooltip — reused by the in-message summary AND the
// threads-list card footer (single source so labels/markup change in one place).
export const ThreadMetaItem = ({ icon, iconType, value, tooltip }) => (
    <Tooltip title={tooltip}>
        <MetaItem><Icon name={icon} type={iconType} size="sm" />{value}</MetaItem>
    </Tooltip>
);

ThreadMetaItem.propTypes = {
    icon: PropTypes.string.isRequired,
    iconType: PropTypes.string,
    value: PropTypes.node,
    tooltip: PropTypes.string,
};

const ThreadSummary = ({ message, onOpenThread }) => {
    const handleOpen = useCallback(() => onOpenThread(message), [onOpenThread, message]);
    if (!message?.tcount) return null;
    const participants = (message.replies || []).length;
    return (
        <SummaryRow className="thread-summary">
            <ReplyButton variant="contained" color="primary" size="small" onClick={handleOpen}>REPLY</ReplyButton>
            {message.tlm && (
                <ThreadMetaItem icon="clock-outline" value={formatThreadTime(message.tlm)} tooltip={formatThreadDateTime(message.tlm)} />
            )}
            <ThreadMetaItem icon="thread" iconType="af" value={message.tcount} tooltip={THREAD_META_TOOLTIP.replies} />
            <ThreadMetaItem icon="account" value={participants} tooltip={THREAD_META_TOOLTIP.participants} />
        </SummaryRow>
    );
};

ThreadSummary.propTypes = {
    message: PropTypes.object,
    onOpenThread: PropTypes.func.isRequired,
};

export default memo(ThreadSummary);
